import React from 'react';
import { User, Award, Music, Heart, Clock, Star, MapPin, Wrench } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const HistoirePage = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const scrollRef3 = useScrollAnimation();
  const scrollRef4 = useScrollAnimation();

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Notre <span className="heading-accent">Histoire</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              L'histoire de Jean-Claude Buchta et de Musique Buchta :
              plus de quarante ans de passion au service de la musique alsacienne.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal max-w-4xl mx-auto"
          >
            <div className="glass-card rounded-3xl p-12">
              <div className="flex items-center justify-center mb-8">
                <User className="h-16 w-16 text-yellow-400" />
              </div>
              <p className="text-white/80 font-light text-lg leading-relaxed text-center">
                Depuis plus de quarante ans, <span className="text-yellow-400 font-semibold">Jean-Claude Buchta</span> est une référence incontournable dans la région pour l'entretien, la restauration et la vente d'instruments de musique. Installé à Rixheim, il a fondé <span className="heading-accent font-semibold">Musique Buchta</span>, un magasin où l'on trouve aussi bien des pianos droits et à queue que des guitares et des didgeridoos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Savoir-faire */}
      <section className="py-20 bg-gradient-to-b from-transparent via-gray-900/30 to-transparent">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef3 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h2 className="text-4xl font-light text-center text-white mb-6">
              Un Savoir-Faire <span className="heading-accent">Forgé par l'Expérience</span>
            </h2>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-16"></div>
            
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="scroll-reveal relative">
                <div className="relative overflow-hidden rounded-3xl">
                  <img
                    src="/jean_claude_buchta.jpg"
                    alt="Jean-Claude Buchta avec ses pianos"
                    className="w-full aspect-square object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="glass-card rounded-2xl p-8">
                  <div className="flex items-start space-x-6">
                    <MapPin className="h-8 w-8 text-yellow-400 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-4">Débuts en Suisse</h3>
                      <p className="text-white/70 font-light leading-relaxed">
                        Jean-Claude Buchta a commencé sa vie professionnelle en Suisse, en travaillant dans une grande chaîne de magasins de musique. Passionné par les instruments et le son, il s'est spécialisé dans l'accordage et est rapidement devenu accordeur au Conservatoire de Bâle.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="glass-card rounded-2xl p-8">
                  <div className="flex items-start space-x-6">
                    <Award className="h-8 w-8 text-yellow-400 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-4">Formation d'Excellence</h3>
                      <p className="text-white/70 font-light leading-relaxed">
                        Afin de perfectionner sa technique, il a suivi des stages de formation auprès de deux manufactures emblématiques du piano : <span className="text-yellow-400 font-semibold">Steinway et Bechstein</span>. Ces apprentissages lui ont permis d'acquérir une expertise reconnue dans l'harmonisation, le réglage et la restauration d'instruments.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Artisan au service */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-light text-center text-white mb-6">
              Un Artisan au Service <span className="heading-accent">des Musiciens</span>
            </h2>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-16"></div>
            
            <div className="glass-card rounded-3xl p-12">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="relative">
                  <img
                    src="/image2.jpg"
                    alt="Pianiste en concert sur piano à queue"
                    className="w-full h-64 object-cover rounded-2xl"
                  />
                </div>
                
                <div>
                  <div className="flex items-center space-x-4 mb-6">
                    <Music className="h-10 w-10 text-yellow-400" />
                    <h3 className="text-2xl font-semibold text-white">Pianos de Concert</h3>
                  </div>
                  <p className="text-white/70 font-light leading-relaxed mb-6">
                    Aujourd'hui, Jean-Claude Buchta continue d'exercer un métier rare et précieux : l'entretien de pianos de concert. Depuis de nombreuses années, il est sollicité par plusieurs salles de spectacles de la région, comme <span className="text-yellow-400 font-semibold">Les Dominicains à Guebwiller</span>, <span className="text-yellow-400 font-semibold">Le Théâtre de la Sinne</span> et <span className="text-yellow-400 font-semibold">La Filature</span>.
                  </p>
                  <p className="text-white/60 font-light leading-relaxed">
                    Chaque piano qu'il accorde ou restaure est traité avec précision, patience et respect : réglage mécanique, harmonisation, accordage traditionnel ou remise en état complète.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {[
                    "Réglage mécanique de précision",
                    "Harmonisation professionnelle", 
                    "Accordage traditionnel à l'oreille",
                    "Remise en état complète"
                  ].map((service, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <Star className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                      <span className="text-white/80 font-light">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Musique Buchta */}
      <section className="py-20 bg-gradient-to-b from-transparent via-gray-900/30 to-transparent">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef4 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <h2 className="text-4xl font-light text-center text-white mb-6">
              Musique Buchta : <span className="heading-accent">Plus qu'un Magasin</span>
            </h2>
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-16"></div>
            
            <div className="max-w-4xl mx-auto mb-12">
              <div className="glass-card rounded-3xl p-8 text-center">
                <MapPin className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                <p className="text-white/80 font-light text-lg">
                  Installée au <span className="text-yellow-400 font-semibold">122 rue de l'Île-Napoléon à Rixheim</span>, la boutique Musique Buchta propose :
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Music,
                  title: "Vente de Pianos",
                  description: "Pianos neufs ou d'occasion"
                },
                {
                  icon: Star,
                  title: "Instruments Variés",
                  description: "Instruments à corde, à vent, percussions et instruments traditionnels"
                },
                {
                  icon: Clock,
                  title: "Location",
                  description: "Location de pianos pour concerts ou événements"
                },
                {
                  icon: Wrench,
                  title: "Réparation",
                  description: "Réparation, restauration et service après-vente"
                },
                {
                  icon: Heart,
                  title: "Conseils",
                  description: "Conseils personnalisés pour débutants, amateurs, écoles et professionnels"
                },
                {
                  icon: Award,
                  title: "Qualité",
                  description: "Service de qualité adapté aux besoins de chaque musicien"
                }
              ].map((service, index) => (
                <div key={index} className="glass-card interactive-card rounded-2xl p-6 text-center light-effect">
                  <service.icon className="h-10 w-10 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-white/60 font-light text-sm leading-relaxed">{service.description}</p>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <div className="glass-card rounded-3xl p-8 max-w-3xl mx-auto">
                <p className="text-white/70 font-light text-lg leading-relaxed">
                  L'objectif de l'enseigne est simple : <span className="text-yellow-400 font-semibold">offrir un service de qualité, adapté aux besoins de chaque musicien</span>, qu'il s'agisse d'un premier instrument ou d'un piano de concert.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transmission */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto text-center">
            <Heart className="h-16 w-16 text-yellow-400 mx-auto mb-8" />
            <h3 className="text-3xl font-light text-white mb-6">
              Une Passion qui se <span className="heading-accent">Transmet</span>
            </h3>
            <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
              Après plusieurs décennies d'activité, Jean-Claude Buchta continue d'exercer avec la même passion et souhaite transmettre son savoir-faire aux futures générations.
            </p>
            <div className="bg-gradient-to-r from-yellow-400/20 to-yellow-400/10 rounded-2xl p-6 border border-yellow-400/30">
              <p className="text-white/80 font-light leading-relaxed">
                L'accordage et la restauration de pianos sont des métiers rares, qui exigent une oreille précise, de la patience et une grande finesse technique — <span className="text-yellow-400 font-semibold">des valeurs qu'il défend chaque jour</span>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HistoirePage;